import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

type Tipo = 'nfe' | 'cteos' | 'nfse-nacional' | 'nfse-infisc'
type Formato = 'xml' | 'pdf' | 'ambos'

interface Linha {
  id: number | null
  serie: string
  numero: number
  chave: string
  data: string
  nome: string
  documento: string
  valor: string
  situacao: string
  selecionado: boolean
}

const TIPOS: { valor: Tipo; rotulo: string }[] = [
  { valor: 'nfe',           rotulo: 'NF-e' },
  { valor: 'cteos',         rotulo: 'CT-e OS' },
  { valor: 'nfse-nacional', rotulo: 'NFS-e Nacional' },
  { valor: 'nfse-infisc',   rotulo: 'NFS-e INFISC' },
]

// NF-e/CT-e filtram por texto; NFS-e pelo código numérico (sitnfe)
const SITUACOES: Record<Tipo, { valor: string; rotulo: string }[]> = {
  'nfe':           [{ valor: 'AUTORIZADA', rotulo: 'Autorizada' }, { valor: 'CANCELADA', rotulo: 'Cancelada' }, { valor: 'REJEITADA', rotulo: 'Rejeitada' }, { valor: 'LIBERADA_ENVIO', rotulo: 'Pendente' }],
  'cteos':         [{ valor: 'AUTORIZADA', rotulo: 'Autorizada' }, { valor: 'CANCELADA', rotulo: 'Cancelada' }, { valor: 'REJEITADA', rotulo: 'Rejeitada' }, { valor: 'LIBERADA_ENVIO', rotulo: 'Pendente' }],
  'nfse-nacional': [{ valor: '6', rotulo: 'Autorizada' }, { valor: '7', rotulo: 'Cancelada' }, { valor: '4', rotulo: 'Rejeitada' }, { valor: '5', rotulo: 'Pendente' }],
  'nfse-infisc':   [{ valor: '6', rotulo: 'Autorizada' }, { valor: '7', rotulo: 'Cancelada' }, { valor: '4', rotulo: 'Rejeitada' }, { valor: '5', rotulo: 'Pendente' }],
}
const SIT_NFSE: Record<number, string> = { 4: 'REJEITADA', 5: 'PENDENTE', 6: 'AUTORIZADA', 7: 'CANCELADA' }

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function inicioMesISO() { return hojeISO().slice(0, 8) + '01' }
function fmtDataBr(v: string) { return v ? v.slice(0, 10).split('-').reverse().join('/') : '-' }
function fmtMoeda(v: string) {
  const n = parseFloat(v)
  return isNaN(n) ? '-' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function corSituacao(s: string) {
  if (s === 'AUTORIZADA') return { bg: '#dcfce7', fg: '#15803d' }
  if (s === 'CANCELADA')  return { bg: '#fee2e2', fg: '#dc2626' }
  if (s === 'REJEITADA')  return { bg: '#ffedd5', fg: '#c2410c' }
  return { bg: '#fef3c7', fg: '#b45309' }
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

export default function DownloadLoteNotas() {
  const { empresaCod } = useEmpresa()
  const empcod = empresaCod ?? 1
  const [tipo, setTipo] = useState<Tipo>('nfe')
  const [dataInicio, setDataInicio] = useState(inicioMesISO())
  const [dataFim, setDataFim] = useState(hojeISO())
  const [numero, setNumero] = useState('')
  const [cliente, setCliente] = useState('')
  const [situacao, setSituacao] = useState('')
  const [formato, setFormato] = useState<Formato>('ambos')

  const [buscando, setBuscando] = useState(false)
  const [gerando, setGerando] = useState(false)
  const [erro, setErro] = useState('')
  const [linhas, setLinhas] = useState<Linha[]>([])
  const [tipoBuscado, setTipoBuscado] = useState<Tipo>('nfe')

  function trocarTipo(t: Tipo) {
    setTipo(t); setSituacao(''); setLinhas([]); setErro('')
  }

  async function buscar() {
    setErro(''); setBuscando(true); setLinhas([])
    try {
      const nfse = tipo === 'nfse-nacional' || tipo === 'nfse-infisc'
      const params: Record<string, string | number> = { dataInicio, dataFim }
      if (numero.trim()) params.numero = numero.trim()
      if (nfse) {
        params.empcod = empcod
        if (cliente.trim()) params.tomador = cliente.trim()
        if (situacao) params.sitnfe = situacao
      } else {
        if (cliente.trim()) params.cliente = cliente.trim()
        if (situacao) params.situacao = situacao
      }
      const url = tipo === 'nfe' ? '/nfe' : tipo === 'cteos' ? '/cte-os' : `/${tipo}/lista`
      const r = await api.get(url, { params })
      const dados: any[] = r.data ?? []
      setLinhas(dados.map(d => ({
        id:        d.id ?? null,
        serie:     String(d.serie ?? '').trim(),
        numero:    Number(d.numero),
        chave:     String(d.chave ?? d.chave_acesso ?? '').trim(),
        data:      d.data_emissao ?? d.data_compet ?? '',
        nome:      d.nome_dest ?? d.nome_tomador ?? '',
        documento: d.cnpj_dest ?? d.cnpj_tomador ?? '',
        valor:     d.valor_total ?? '',
        situacao:  nfse ? (SIT_NFSE[Number(d.sitnfe)] ?? String(d.sitnfe)) : (d.situacao === 'LIBERADA_ENVIO' ? 'PENDENTE' : String(d.situacao ?? '')),
        selecionado: false,
      })))
      setTipoBuscado(tipo)
      if (dados.length === 0) setErro('Nenhuma nota encontrada com esses filtros.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar notas.')
    } finally {
      setBuscando(false)
    }
  }

  function marcarTodos(v: boolean) { setLinhas(prev => prev.map(l => ({ ...l, selecionado: v }))) }
  function toggleLinha(idx: number) { setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, selecionado: !l.selecionado } : l)) }

  async function baixarSelecionadas() {
    const sel = linhas.filter(l => l.selecionado)
    if (sel.length === 0) { setErro('Marque ao menos uma nota.'); return }
    setErro(''); setGerando(true)
    try {
      const resp = await api.post('/download-lote',
        { tipo: tipoBuscado, formato, empcod, notas: sel.map(l => ({ id: l.id, numero: l.numero, chave: l.chave })) },
        { responseType: 'blob' },
      )
      const rotulo = TIPOS.find(t => t.valor === tipoBuscado)?.rotulo.replace(/\W+/g, '') ?? 'notas'
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `${rotulo}_${formato}_${hojeISO()}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      setErro('Erro ao gerar o arquivo ZIP.')
    } finally {
      setGerando(false)
    }
  }

  const todasMarcadas = linhas.length > 0 && linhas.every(l => l.selecionado)
  const qtdMarcadas = linhas.filter(l => l.selecionado).length
  const nfseBuscado = tipoBuscado === 'nfse-nacional' || tipoBuscado === 'nfse-infisc'

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.4rem' }}>Download de XML/PDF em Lote</h2>
      <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
        Escolha o tipo de nota, marque as que quiser e baixe XML, PDF ou os dois num único arquivo .zip.
      </p>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Tipo de Nota" width={160}>
            <select style={inp} value={tipo} onChange={e => trocarTipo(e.target.value as Tipo)}>
              {TIPOS.map(t => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
            </select>
          </Campo>
          <Campo rotulo="Período De" width={150}>
            <input style={inp} type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
          </Campo>
          <Campo rotulo="Até" width={150}>
            <input style={inp} type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
          </Campo>
          <Campo rotulo="Nº da Nota" width={110}>
            <input style={inp} inputMode="numeric" maxLength={9} value={numero} onChange={e => setNumero(e.target.value.replace(/\D/g, ''))} placeholder="Todas" />
          </Campo>
          <Campo rotulo={tipo.startsWith('nfse') || tipo === 'cteos' ? 'Tomador' : 'Cliente'} width={260}>
            <input style={inp} value={cliente} onChange={e => setCliente(e.target.value)} placeholder="Nome ou CNPJ" maxLength={60} />
          </Campo>
          <Campo rotulo="Situação" width={140}>
            <select style={inp} value={situacao} onChange={e => setSituacao(e.target.value)}>
              <option value="">Todas</option>
              {SITUACOES[tipo].map(s => <option key={s.valor} value={s.valor}>{s.rotulo}</option>)}
            </select>
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscando} onClick={buscar} style={{ height: 32 }}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {linhas.length > 0 && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
            <div style={{ display: 'flex', gap: '0.9rem', fontSize: '0.85rem' }}>
              {(['xml', 'pdf', 'ambos'] as Formato[]).map(f => (
                <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="formato" checked={formato === f} onChange={() => setFormato(f)} />
                  {f === 'xml' ? 'Só XML' : f === 'pdf' ? 'Só PDF' : 'XML + PDF'}
                </label>
              ))}
            </div>
            <button className="btn btn-primary btn-sm" onClick={baixarSelecionadas} disabled={gerando || qtdMarcadas === 0} style={{ height: 32 }}>
              {gerando ? 'Gerando...' : `Baixar Selecionadas (${qtdMarcadas})`}
            </button>
            <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>{linhas.length} nota(s) encontrada(s)</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: '0.78rem', marginBottom: '0.5rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', width: 40 }}>
                    <input type="checkbox" checked={todasMarcadas} onChange={e => marcarTodos(e.target.checked)} />
                  </th>
                  <th style={{ textAlign: 'center', width: 60 }}>Série</th>
                  <th style={{ textAlign: 'center', width: 90 }}>Número</th>
                  <th style={{ textAlign: 'center', width: 90 }}>{nfseBuscado ? 'Competência' : 'Emissão'}</th>
                  <th style={{ textAlign: 'left' }}>{tipoBuscado === 'nfe' ? 'Cliente' : 'Tomador'}</th>
                  <th style={{ textAlign: 'right', width: 110 }}>Valor</th>
                  <th style={{ textAlign: 'center', width: 110 }}>Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => {
                  const cor = corSituacao(l.situacao)
                  return (
                    <tr key={`${l.serie}-${l.numero}-${l.chave}`} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff', cursor: 'pointer' }} onClick={() => toggleLinha(idx)}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={l.selecionado} onChange={() => toggleLinha(idx)} onClick={e => e.stopPropagation()} />
                      </td>
                      <td style={{ textAlign: 'center', color: '#374151' }}>{l.serie}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#1a6ea8' }}>{l.numero}</td>
                      <td style={{ textAlign: 'center', color: '#374151' }}>{fmtDataBr(l.data)}</td>
                      <td style={{ color: '#374151' }}>{l.nome || l.documento}</td>
                      <td style={{ textAlign: 'right', color: '#374151' }}>{fmtMoeda(l.valor)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, background: cor.bg, color: cor.fg }}>
                          {l.situacao}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
